import React, { useEffect, useMemo, useState } from "react";
import { api, inr, rangeFromKey } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import DateRangeFilter from "@/components/DateRangeFilter";
import { Stethoscope, Briefcase, BookOpen, MessageCircleHeart, Sparkles, TrendingUp, TrendingDown, Minus, Download } from "lucide-react";

const GROUPS = [
  { key: "patient_work", label: "Patient Work", icon: Stethoscope, tint: "text-emerald_on" },
  { key: "business", label: "Business Contribution", icon: Briefcase, tint: "gold-text" },
  { key: "knowledge", label: "Knowledge / Content", icon: BookOpen, tint: "gold-text" },
  { key: "patient_experience", label: "Patient Experience", icon: MessageCircleHeart, tint: "text-emerald_on" },
  { key: "organizational", label: "Organizational", icon: Sparkles, tint: "text-muted-foreground" },
];

const QUICK_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  this_week: "This Week",
  this_month: "This Month",
  last_month: "Last Month",
  custom: "Custom",
};

export default function Dashboard() {
  const { user, doctor } = useAuth();
  const isAdmin = ["founder","manager"].includes(user?.role);
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selDoc, setSelDoc] = useState(null);
  const [selBranch, setSelBranch] = useState("all");
  const [range, setRange] = useState({ key: "this_month", ...rangeFromKey("this_month") });
  const [util, setUtil] = useState(null);
  const [mgmt, setMgmt] = useState(null);
  const [targets, setTargets] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [trend, setTrend] = useState([]);
  const [prev, setPrev] = useState(null); // previous-equivalent-period aggregates
  const [prevRange, setPrevRange] = useState(null);

  useEffect(() => {
    api.get("/doctors").then(r => {
      setDoctors(r.data);
      if (user?.role === "doctor" && doctor) setSelDoc(doctor.doctor_id);
      else if (r.data[0]) setSelDoc(r.data[0].doctor_id);
    });
    api.get("/branches").then(r => setBranches(r.data));
  }, [user, doctor]);

  useEffect(() => {
    if (!selDoc) return;
    const fetch = async () => {
      try {
        const [w, m, ach] = await Promise.all([
          api.get(`/utilization/summary?doctor_id=${selDoc}&period=weekly`),
          api.get(`/utilization/summary?doctor_id=${selDoc}&period=monthly`),
          api.get(`/targets/achievement?doctor_id=${selDoc}`),
        ]);
        setWeekly(w.data);
        setMonthly(m.data);
        setTargets(ach.data);
      } catch {}
    };
    fetch();
  }, [selDoc]);

  useEffect(() => {
    if (!selDoc || !range?.from || !range?.to) return;
    const actTypes = ["ppt","shoots","training","education"];
    Promise.all([
      api.get(`/daily-logs?doctor_id=${selDoc}&date_from=${range.from}&date_to=${range.to}`),
      ...actTypes.map(t => api.get(`/activities?type=${t}&doctor_id=${selDoc}&date_from=${range.from}&date_to=${range.to}`).then(r => r.data).catch(() => [])),
    ]).then(([logsRes, pptActs, shootActs, trainActs, eduActs]) => {
      const logs = (logsRes.data || []).sort((a,b) => a.date.localeCompare(b.date));
      const series = logs.map(l => {
        const d = l.data || {};
        return {
          date: l.date,
          consultations: Number(d.total_consultations || d.walkins || 0),
          treatment: Number(d.treatment_taken ?? d.patients_treatment ?? d.consultations_procedure ?? 0),
          crosssell: Number(d.crosssell_suggested || 0),
          ppts: Number(d.ppts_made || 0),
          shoots: Number(d.educational_shoots || 0),
          videos: Number(d.informative_videos || 0),
          teaching: Number(d.teaching_sessions || 0),
        };
      });
      setTrend(series);
      let intentTotal = 0, takenTotal = 0;
      logs.forEach(l => {
        const d = l.data || {};
        if (d.treatment_intent !== undefined || d.treatment_taken !== undefined) {
          intentTotal += Number(d.treatment_intent || 0);
          takenTotal += Number(d.treatment_taken || 0);
        }
      });
      const sumCount = (arr) => (arr || []).reduce((s, a) => {
        const c = a?.data?.count;
        return s + (c === undefined || c === null || c === "" ? 1 : (Number(c) || 0));
      }, 0);
      const doneOnly = (arr) => (arr || []).filter(a => a?.data?.completed === undefined || a?.data?.completed === "Y");
      const teachTypes = ["Student Session","Institute Session"];
      const trainDone = doneOnly(trainActs);
      const agg = {
        consultations: series.reduce((s,x) => s + x.consultations, 0),
        treatment: takenTotal + logs.filter(l => (l.data||{}).treatment_intent === undefined && (l.data||{}).treatment_taken === undefined)
          .reduce((s,l) => s + Number((l.data||{}).patients_treatment ?? (l.data||{}).consultations_procedure ?? 0), 0),
        crosssell_suggested: series.reduce((s,x) => s + x.crosssell, 0),
        crosssell_amount: logs.reduce((s,l) => s + Number(l.data?.crosssell_amount || 0), 0),
        complaints: logs.reduce((s,l) => s + Number(l.data?.complaints || 0), 0),
        complaints_resolved: logs.reduce((s,l) => s + (Array.isArray(l.data?.complaints_list) ? l.data.complaints_list.filter(c => c.resolved === "Y").length : 0), 0),
        events_camps: logs.reduce((s,l) => s + Number(l.data?.events_count || (l.data?.event_organized === "Y" ? 1 : 0)), 0),
        ppts_made: series.reduce((s,x) => s + x.ppts, 0) + sumCount(pptActs),
        educational_shoots: series.reduce((s,x) => s + x.shoots, 0) + sumCount(doneOnly(shootActs)),
        informative_videos: series.reduce((s,x) => s + x.videos, 0) + (eduActs || []).filter(a => (a?.data?.type || "") === "Video").length,
        teaching_sessions: series.reduce((s,x) => s + x.teaching, 0) + trainDone.filter(a => teachTypes.includes(a?.data?.training_type)).length,
        training_meetings: logs.reduce((s,l) => s + (l.data?.training_meeting === "Y" ? 1 : 0), 0) + trainDone.filter(a => !teachTypes.includes(a?.data?.training_type)).length,
        days_logged: logs.length,
      };
      agg.conversion = intentTotal > 0 ? Math.round((takenTotal / intentTotal) * 10000) / 100 : null;
      agg.complaint_resolution_rate = agg.complaints ? Math.round((agg.complaints_resolved / agg.complaints) * 10000) / 100 : null;
      setUtil(agg);
    });
  }, [selDoc, range]);

  // Previous-equivalent-period comparison
  useEffect(() => {
    if (!selDoc || !range?.from || !range?.to) { setPrev(null); setPrevRange(null); return; }
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (isNaN(from) || isNaN(to)) return;
    const days = Math.round((to - from) / 86400000) + 1;
    const prevTo = new Date(from); prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo); prevFrom.setDate(prevFrom.getDate() - (days - 1));
    const iso = (d) => d.toISOString().slice(0, 10);
    const pf = iso(prevFrom), pt = iso(prevTo);
    setPrevRange({ from: pf, to: pt });
    api.get(`/daily-logs?doctor_id=${selDoc}&date_from=${pf}&date_to=${pt}`).then(r => {
      const logs = r.data || [];
      if (!logs.length) { setPrev({ __empty: true }); return; }
      const p = {
        consultations: 0, treatment: 0, crosssell_suggested: 0, crosssell_amount: 0,
        complaints: 0, complaints_resolved: 0, events_camps: 0,
        ppts_made: 0, educational_shoots: 0, informative_videos: 0, teaching_sessions: 0,
        training_meetings: 0, days_logged: logs.length,
      };
      logs.forEach(l => {
        const d = l.data || {};
        p.consultations += Number(d.total_consultations || d.walkins || 0);
        p.treatment += Number(d.treatment_taken ?? d.patients_treatment ?? d.consultations_procedure ?? 0);
        if (d.treatment_intent !== undefined || d.treatment_taken !== undefined) {
          p.intent = (p.intent || 0) + Number(d.treatment_intent || 0);
          p.taken = (p.taken || 0) + Number(d.treatment_taken || 0);
        }
        p.crosssell_suggested += Number(d.crosssell_suggested || 0);
        p.crosssell_amount += Number(d.crosssell_amount || 0);
        p.complaints += Number(d.complaints || 0);
        p.complaints_resolved += Array.isArray(d.complaints_list) ? d.complaints_list.filter(c => c.resolved === "Y").length : 0;
        p.events_camps += Number(d.events_count || (d.event_organized === "Y" ? 1 : 0));
        p.ppts_made += Number(d.ppts_made || 0);
        p.educational_shoots += Number(d.educational_shoots || 0);
        p.informative_videos += Number(d.informative_videos || 0);
        p.teaching_sessions += Number(d.teaching_sessions || 0);
        p.training_meetings += (d.training_meeting === "Y" ? 1 : 0);
      });
      p.conversion = (p.intent || 0) > 0 ? Math.round(((p.taken || 0) / p.intent) * 10000) / 100 : null;
      setPrev(p);
    }).catch(() => setPrev({ __empty: true }));
  }, [selDoc, range]);

  useEffect(() => {
    if (!isAdmin || !range?.from || !range?.to) return;
    const branch = selBranch === "all" ? "" : selBranch;
    api.get(`/utilization/management?date_from=${range.from}&date_to=${range.to}${branch ? `&branch=${encodeURIComponent(branch)}` : ""}`).then(r => setMgmt(r.data)).catch(() => {});
  }, [isAdmin, range, selBranch]);

  const canPickDoctor = user?.role !== "doctor";
  const rangeLabel = useMemo(() => {
    if (!range) return "";
    return range.key === "custom" ? `${range.from} → ${range.to}` : QUICK_LABELS[range.key] || range.key;
  }, [range]);

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Doctor Utilization Dashboard</div>
          <h1 className="mt-2 text-3xl font-heading font-medium">
            {doctors.find(d => d.doctor_id === selDoc)?.name || doctor?.name || "Overview"}
          </h1>
          <div className="gold-underline mt-3" />
        </div>
        <div className="flex flex-wrap gap-3">
          {canPickDoctor && (
            <>
              <Select value={selDoc || ""} onValueChange={setSelDoc}>
                <SelectTrigger className="w-56 bg-navy-800 border-navy-700" data-testid="dashboard-doctor-select"><SelectValue placeholder="Doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map(d => <SelectItem key={d.doctor_id} value={d.doctor_id}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={selBranch} onValueChange={setSelBranch}>
                <SelectTrigger className="w-48 bg-navy-800 border-navy-700" data-testid="dashboard-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.branch_id} value={b.name}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
        </div>
      </div>

      <DateRangeFilter value={range} onChange={setRange} />
      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 items-center">
        <span>Showing: <span className="gold-text">{rangeLabel}</span></span>
        {prevRange && (
          <span data-testid="growth-compare-range">
            vs previous period <span className="text-slate-300">{prevRange.from} → {prevRange.to}</span>
            {prev?.__empty && <span className="ml-2 rag-red text-[10px] uppercase tracking-widest px-2 py-0.5 rounded" data-testid="no-previous-period">No Previous Period Data</span>}
          </span>
        )}
      </div>

      {util && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GROUPS.map(g => (
            <Card key={g.key} className="bg-navy-800 border-navy-700" data-testid={`group-${g.key}`}>
              <CardHeader className="border-b border-navy-700 flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-base">{g.label}</CardTitle>
                <g.icon className={`h-4 w-4 ${g.tint}`} />
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {g.key === "patient_work" && (
                  <>
                    <StatRow label="Consultations" value={util.consultations} prev={prev} field="consultations" />
                    <StatRow label="Treatment Patients" value={util.treatment} prev={prev} field="treatment" />
                    <StatRow label="Treatment Conversion" value={util.conversion === null ? "N/A" : `${util.conversion}%`} prev={util.conversion === null ? null : prev} field="conversion" />
                  </>
                )}
                {g.key === "business" && (
                  <>
                    <StatRow label="Cross-Sell Suggested" value={util.crosssell_suggested} prev={prev} field="crosssell_suggested" />
                    <StatRow label="Cross-Sell Amount" value={isAdmin ? inr(util.crosssell_amount) : "—"} prev={isAdmin ? prev : null} field="crosssell_amount" isCurrency />
                  </>
                )}
                {g.key === "knowledge" && (
                  <>
                    <StatRow label="PPTs Made" value={util.ppts_made} prev={prev} field="ppts_made" />
                    <StatRow label="Educational Shoots" value={util.educational_shoots} prev={prev} field="educational_shoots" />
                    <StatRow label="Informative Videos" value={util.informative_videos} prev={prev} field="informative_videos" />
                    <StatRow label="Teaching Sessions" value={util.teaching_sessions} prev={prev} field="teaching_sessions" />
                  </>
                )}
                {g.key === "patient_experience" && (
                  <>
                    <StatRow label="Complaints" value={util.complaints} prev={prev} field="complaints" invert />
                    <StatRow label="Resolved" value={util.complaints_resolved} prev={prev} field="complaints_resolved" />
                    <StatRow label="Resolution Rate" value={util.complaint_resolution_rate === null ? "—" : `${util.complaint_resolution_rate}%`} />
                  </>
                )}
                {g.key === "organizational" && (
                  <>
                    <StatRow label="Events / Camps" value={util.events_camps} prev={prev} field="events_camps" />
                    <StatRow label="Training Meetings" value={util.training_meetings} prev={prev} field="training_meetings" />
                    <StatRow label="Days Logged" value={util.days_logged} prev={prev} field="days_logged" />
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="bg-navy-800 border-navy-700">
        <CardHeader className="border-b border-navy-700">
          <CardTitle className="font-heading text-lg">Utilization Trend</CardTitle>
          <div className="text-xs text-muted-foreground">Consultations, cross-sell & knowledge contribution over the selected range.</div>
        </CardHeader>
        <CardContent className="p-4 h-72">
          {trend.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 11 }} />
                <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B" }} />
                <Line type="monotone" dataKey="consultations" stroke="#059669" strokeWidth={2} dot={false} name="Consultations" />
                <Line type="monotone" dataKey="crosssell" stroke="#C9A24B" strokeWidth={2} dot={false} name="Cross-sell" />
                <Line type="monotone" dataKey="ppts" stroke="#60a5fa" strokeWidth={2} dot={false} name="PPTs" />
                <Line type="monotone" dataKey="shoots" stroke="#f59e0b" strokeWidth={2} dot={false} name="Shoots" />
                <Line type="monotone" dataKey="videos" stroke="#a78bfa" strokeWidth={2} dot={false} name="Videos" />
                <Line type="monotone" dataKey="teaching" stroke="#34d399" strokeWidth={2} dot={false} name="Teaching" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full grid place-items-center text-sm text-muted-foreground">No logs in this period yet.</div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {weekly && (
          <Card className="bg-navy-800 border-navy-700" data-testid="weekly-card">
            <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Weekly Utilization <span className="text-xs text-muted-foreground">({weekly.label})</span></CardTitle></CardHeader>
            <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
              <MiniStat label="Consultations" value={weekly.patient_work.consultations} />
              <MiniStat label="Conversion" value={`${weekly.patient_work.conversion_rate}%`} />
              <MiniStat label="Cross-Sell Suggested" value={weekly.business.crosssell_suggested} />
              <MiniStat label="PPTs" value={weekly.knowledge.ppts_made} />
              <MiniStat label="Shoots" value={weekly.knowledge.educational_shoots} />
              <MiniStat label="Videos" value={weekly.knowledge.informative_videos} />
              <MiniStat label="Teaching" value={weekly.knowledge.teaching_sessions} />
              <MiniStat label="Events" value={weekly.organizational.events_camps} />
              <MiniStat label="Complaints" value={weekly.patient_experience.complaints} />
              <MiniStat label="Training Meetings" value={weekly.organizational.training_meetings} />
            </CardContent>
          </Card>
        )}
        {monthly && (
          <Card className="bg-navy-800 border-navy-700" data-testid="monthly-card">
            <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Monthly Utilization <span className="text-xs text-muted-foreground">({monthly.label})</span></CardTitle></CardHeader>
            <CardContent className="p-4 grid grid-cols-2 gap-3 text-sm">
              <MiniStat label="Consultations" value={monthly.patient_work.consultations} />
              <MiniStat label="Treatment Patients" value={monthly.patient_work.treatment_patients} />
              <MiniStat label="Conversion" value={`${monthly.patient_work.conversion_rate}%`} />
              <MiniStat label="Cross-Sell Suggested" value={monthly.business.crosssell_suggested} />
              <MiniStat label="PPTs" value={monthly.knowledge.ppts_made} />
              <MiniStat label="Shoots" value={monthly.knowledge.educational_shoots} />
              <MiniStat label="Videos" value={monthly.knowledge.informative_videos} />
              <MiniStat label="Teaching" value={monthly.knowledge.teaching_sessions} />
              <MiniStat label="Complaints" value={monthly.patient_experience.complaints} />
              <MiniStat label="Resolution" value={`${monthly.patient_experience.complaint_resolution_rate}%`} />
            </CardContent>
          </Card>
        )}
      </div>

      {targets && (
        <Card className="bg-navy-800 border-gold/40">
          <CardHeader className="border-b border-navy-700"><CardTitle className="font-heading text-base">Educational Contribution — Target Achievement <span className="text-xs text-muted-foreground">({targets.period})</span></CardTitle></CardHeader>
          <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "PPTs", a: targets.actual.ppts_made, t: targets.targets.ppt_target, p: targets.achievement.ppts_made },
              { label: "Educational Shoots", a: targets.actual.educational_shoots, t: targets.targets.shoots_target, p: targets.achievement.educational_shoots },
              { label: "Informative Videos", a: targets.actual.informative_videos, t: targets.targets.videos_target, p: targets.achievement.informative_videos },
              { label: "Teaching Sessions", a: targets.actual.teaching_sessions, t: targets.targets.teaching_target, p: targets.achievement.teaching_sessions },
            ].map(x => (
              <div key={x.label} className="border border-navy-700 rounded-md p-4" data-testid={`target-${x.label.replace(/\s+/g,'-').toLowerCase()}`}>
                <div className="flex justify-between items-baseline">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{x.label}</div>
                  <div className="text-xs num gold-text">{x.a} / {x.t}</div>
                </div>
                <div className="mt-2 h-2 bg-navy-700 rounded-full overflow-hidden">
                  <div className="h-full gold-bg transition-all duration-500" style={{ width: `${x.p}%` }} />
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">{x.p}% of target</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {isAdmin && mgmt && (
        <Card className="bg-navy-800 border-navy-700">
          <CardHeader className="border-b border-navy-700">
            <CardTitle className="font-heading text-lg">All Doctors — {rangeLabel}</CardTitle>
            <div className="text-xs text-muted-foreground">Doctor · Branch · Consultations · Conversion · Cross-sell · Knowledge contribution.</div>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-900/60 uppercase text-[10px] tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-left px-5 py-3">Doctor</th>
                  <th className="text-left px-5 py-3">Branch</th>
                  <th className="text-right px-5 py-3">Consult</th>
                  <th className="text-right px-5 py-3">Treat</th>
                  <th className="text-right px-5 py-3">Conv %</th>
                  <th className="text-right px-5 py-3">Cross-sell</th>
                  <th className="text-right px-5 py-3">Cross ₹</th>
                  <th className="text-right px-5 py-3">PPT</th>
                  <th className="text-right px-5 py-3">Shoots</th>
                  <th className="text-right px-5 py-3">Videos</th>
                  <th className="text-right px-5 py-3">Teach</th>
                  <th className="text-right px-5 py-3">Events</th>
                  <th className="text-right px-5 py-3">Complaints</th>
                </tr>
              </thead>
              <tbody>
                {mgmt.rows.length === 0 && (
                  <tr><td colSpan={13} className="px-5 py-6 text-muted-foreground text-center">No data for this filter yet.</td></tr>
                )}
                {mgmt.rows.map(r => (
                  <tr key={r.doctor_id} className="border-t border-navy-700" data-testid={`mgmt-row-${r.doctor_id}`}>
                    <td className="px-5 py-3">{r.name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{r.branch}</td>
                    <td className="px-5 py-3 text-right num">{r.consultations}</td>
                    <td className="px-5 py-3 text-right num">{r.treatment_patients}</td>
                    <td className="px-5 py-3 text-right num">{r.conversion_rate}%</td>
                    <td className="px-5 py-3 text-right num">{r.crosssell_suggested}</td>
                    <td className="px-5 py-3 text-right num gold-text">{inr(r.crosssell_amount)}</td>
                    <td className="px-5 py-3 text-right num">{r.ppts_made}</td>
                    <td className="px-5 py-3 text-right num">{r.educational_shoots}</td>
                    <td className="px-5 py-3 text-right num">{r.informative_videos}</td>
                    <td className="px-5 py-3 text-right num">{r.teaching_sessions}</td>
                    <td className="px-5 py-3 text-right num">{r.events_camps}</td>
                    <td className="px-5 py-3 text-right num">{r.complaints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <ManagementReports range={range} selBranch={selBranch} />
      )}
    </div>
  );
}

function ManagementReports({ range, selBranch }) {
  const [fb, setFb] = useState(null);
  const [cs, setCs] = useState(null);
  const [csGroup, setCsGroup] = useState("doctor");
  const [kindFilter, setKindFilter] = useState("all");

  const branchQS = selBranch === "all" ? "" : `&branch=${encodeURIComponent(selBranch)}`;
  const kindCsvQS = kindFilter === "all" ? "" : `&kind=${kindFilter}`;
  const fbCsvUrl = range?.from ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/feedback.csv?date_from=${range.from}&date_to=${range.to}${branchQS}${kindCsvQS}` : "#";
  const csCsvUrl = range?.from ? `${process.env.REACT_APP_BACKEND_URL}/api/reports/crosssell.csv?date_from=${range.from}&date_to=${range.to}&group_by=${csGroup}${branchQS}` : "#";

  useEffect(() => {
    if (!range?.from || !range?.to) return;
    const branch = selBranch === "all" ? "" : selBranch;
    const kindQS = kindFilter === "all" ? "" : `&kind=${kindFilter}`;
    const suffix = `date_from=${range.from}&date_to=${range.to}${branch ? `&branch=${encodeURIComponent(branch)}` : ""}${kindQS}`;
    api.get(`/reports/feedback?${suffix}`).then(r => setFb(r.data)).catch(() => setFb(null));
  }, [range, selBranch, kindFilter]);

  useEffect(() => {
    if (!range?.from || !range?.to) return;
    const branch = selBranch === "all" ? "" : selBranch;
    api.get(`/reports/crosssell?date_from=${range.from}&date_to=${range.to}&group_by=${csGroup}${branch ? `&branch=${encodeURIComponent(branch)}` : ""}`)
      .then(r => setCs(r.data)).catch(() => setCs(null));
  }, [range, selBranch, csGroup]);

  const items = fb?.items || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="bg-navy-800 border-navy-700" data-testid="report-feedback">
        <CardHeader className="border-b border-navy-700 flex flex-row justify-between items-start gap-3">
          <div>
            <CardTitle className="font-heading text-base">Feedback & Complaints Timeline</CardTitle>
            <div className="text-xs text-muted-foreground">Newest first · patient feedback, complaints, manager notes.</div>
          </div>
          <div className="flex gap-2 items-center">
            <a href={fbCsvUrl} download={`feedback_${kindFilter}_${range?.from}_${range?.to}.csv`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest gold-text hover:opacity-80" data-testid="fb-csv-btn" target="_self" rel="noopener">
              <Download className="h-3 w-3" /> CSV
            </a>
            <Select value={kindFilter} onValueChange={setKindFilter}>
              <SelectTrigger className="w-40 bg-navy-900 border-navy-700 text-xs" data-testid="fb-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Kinds</SelectItem>
                <SelectItem value="patient_feedback">Patient Feedback</SelectItem>
                <SelectItem value="complaint">Complaints</SelectItem>
                <SelectItem value="manager_feedback">Manager Notes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {fb && (
            <div className="flex flex-wrap gap-3 px-5 py-3 border-b border-navy-700 text-[11px] uppercase tracking-widest text-muted-foreground">
              <span data-testid="fb-total-patient">Feedback: <span className="gold-text">{fb.totals.patient_feedback}</span></span>
              <span data-testid="fb-total-complaints">Complaints: <span className="gold-text">{fb.totals.complaints}</span> ({fb.totals.complaints_resolved} resolved)</span>
              <span data-testid="fb-total-manager">Manager: <span className="gold-text">{fb.totals.manager_feedback}</span></span>
            </div>
          )}
          <div className="max-h-80 overflow-y-auto divide-y divide-navy-700">
            {items.length === 0 && <div className="p-5 text-sm text-muted-foreground">No feedback or complaints for this range.</div>}
            {items.map((i, idx) => (
              <div key={idx} className="px-5 py-3 text-sm" data-testid={`fb-item-${idx}`}>
                <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-muted-foreground">
                  <span>{i.date} · <span className="text-slate-200">{i.doctor_name}</span> · {i.branch}</span>
                  <span className={
                    i.kind === "complaint"
                      ? (i.resolved ? "rag-green px-2 py-0.5 rounded" : "rag-red px-2 py-0.5 rounded")
                      : (i.kind === "manager_feedback" ? "gold-text" : "text-emerald_on")
                  }>
                    {i.kind === "complaint" ? (i.resolved ? "Complaint · Resolved" : "Complaint · Open") : (i.kind === "manager_feedback" ? "Manager Note" : "Patient Feedback")}
                  </span>
                </div>
                <div className="mt-1 text-slate-100">{i.text}</div>
                {i.from_name && <div className="text-[10px] text-muted-foreground mt-1">by {i.from_name}</div>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-navy-800 border-navy-700" data-testid="report-crosssell">
        <CardHeader className="border-b border-navy-700 flex flex-row justify-between items-start gap-3">
          <div>
            <CardTitle className="font-heading text-base">Cross-Sell Breakdown</CardTitle>
            <div className="text-xs text-muted-foreground">Total suggested ₹{cs ? cs.total_amount.toLocaleString('en-IN') : 0} across {cs?.total_suggested || 0} suggestions.</div>
          </div>
          <div className="flex gap-2 items-center">
            <a href={csCsvUrl} download={`crosssell_${csGroup}_${range?.from}_${range?.to}.csv`} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest gold-text hover:opacity-80" data-testid="cs-csv-btn" target="_self" rel="noopener">
              <Download className="h-3 w-3" /> CSV
            </a>
            <Select value={csGroup} onValueChange={setCsGroup}>
              <SelectTrigger className="w-32 bg-navy-900 border-navy-700 text-xs" data-testid="cs-group-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="doctor">By Doctor</SelectItem>
                <SelectItem value="branch">By Branch</SelectItem>
                <SelectItem value="month">By Month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy-900/60 uppercase text-[10px] tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-5 py-3">{csGroup === "doctor" ? "Doctor" : csGroup === "branch" ? "Branch" : "Month"}</th>
                {csGroup === "doctor" && <th className="text-left px-5 py-3">Branch</th>}
                <th className="text-right px-5 py-3">Suggestions</th>
                <th className="text-right px-5 py-3">Amount ₹</th>
                <th className="text-right px-5 py-3">Avg Ticket</th>
              </tr>
            </thead>
            <tbody>
              {(!cs || cs.rows.length === 0) && (
                <tr><td colSpan={csGroup === "doctor" ? 5 : 4} className="px-5 py-6 text-center text-muted-foreground">No cross-sell activity in this range.</td></tr>
              )}
              {cs?.rows.map(r => (
                <tr key={r.key} className="border-t border-navy-700" data-testid={`cs-row-${r.key}`}>
                  <td className="px-5 py-3">{r.label}</td>
                  {csGroup === "doctor" && <td className="px-5 py-3 text-muted-foreground">{r.branch}</td>}
                  <td className="px-5 py-3 text-right num">{r.crosssell_suggested}</td>
                  <td className="px-5 py-3 text-right num gold-text">{inr(r.crosssell_amount)}</td>
                  <td className="px-5 py-3 text-right num">{inr(r.avg_ticket)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatRow({ label, value, prev, field, invert, suffix, isCurrency }) {
  const showGrowth = prev && !prev.__empty && field && typeof prev[field] === "number";
  let pct = null, dir = "flat", isNew = false;
  if (showGrowth) {
    const cur = Number((typeof value === "string" ? value.replace(/[^\d.-]/g, "") : value) || 0);
    const p = Number(prev[field] || 0);
    if (p === 0 && cur === 0) { dir = "flat"; }
    else if (p === 0) { dir = "up"; isNew = true; }
    else {
      const raw = ((cur - p) / p) * 100;
      pct = Math.round(raw * 10) / 10;
      dir = raw > 0 ? "up" : raw < 0 ? "down" : "flat";
    }
  }
  const good = invert ? (dir === "down") : (dir === "up");
  const bad = invert ? (dir === "up") : (dir === "down");
  const color = good ? "text-emerald_on" : bad ? "text-rose_alert" : "text-muted-foreground";
  const Icon = dir === "up" ? TrendingUp : dir === "down" ? TrendingDown : Minus;
  return (
    <div className="flex justify-between items-baseline" data-testid={field ? `stat-${field}` : undefined}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="flex items-baseline gap-2">
        <div className="num text-lg font-heading">{value}</div>
        {showGrowth && (
          <span className={`inline-flex items-center gap-0.5 text-[10px] ${color}`} data-testid={field ? `growth-${field}` : undefined}>
            <Icon className="h-3 w-3" />
            {isNew ? "new" : (pct === null ? "—" : `${pct > 0 ? "+" : ""}${pct}%`)}
          </span>
        )}
      </div>
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="border border-navy-700 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</div>
      <div className="mt-1 num">{value}</div>
    </div>
  );
}
